import { type ComponentProps, useCallback } from 'react'

import { ButtonDefault } from '@/components/common/Button'
import { IconWithLabel } from '@/components/icons/IconWithLabel'
import { BRAND_LABELS } from '@/defs/brand'
import { type CardIdType, type CardMapType } from '@/defs/card'
import { PRICE_SOURCE_CURRENCIES, type PriceSourceType } from '@/defs/price'
import { trackEvent } from '@/utils/analyticsUtil'
import { getBulkExportLink, getPriceSourceTotal } from '@/utils/brandUtil'
import { formatPrice } from '@/utils/formatUtil'

import styles from './ClipboardBuyLinksButton.module.scss'
import { ClipboardBuyLinksButtonCardsphere } from './ClipboardBuyLinksButtonCardsphere/ClipboardBuyLinksButtonCardsphere'
import { ClipboardBuyLinksButtonSCG } from './ClipboardBuyLinksButtonSCG/ClipboardBuyLinksButtonSCG'

export type ClipboardBuyButtonType = Pick<
  ComponentProps<typeof ButtonDefault>,
  'colorScheme' | 'justifyContent' | 'maxwidth' | 'size'
>

type Props = {
  cardIds: CardIdType[]
  cardMap: CardMapType
  decklist: string[]
  priceSource: PriceSourceType
}

export const ClipboardBuyLinksButton = ({ cardIds, cardMap, decklist, priceSource }: Props) => {
  const { icon, label } = BRAND_LABELS[priceSource]
  const currency = PRICE_SOURCE_CURRENCIES[priceSource]
  const price = getPriceSourceTotal({ cardIds, cardMap, priceSource })
  const url = getBulkExportLink({ brand: priceSource, decklist })

  const isScg = priceSource === 'scg'
  const isCardsphere = priceSource === 'cardsphere'

  const onTrack = useCallback((label: string) => {
    trackEvent({ action: 'clipboard_link', category: 'outbound', label })
  }, [])

  if (!isScg && !isCardsphere && !url) return null

  const content = (
    <span className={styles.container}>
      <IconWithLabel iconBefore={icon} iconBeforeMargin='sm' iconBeforeSize='sm'>
        {label}
      </IconWithLabel>
      <span className={styles.price}>{formatPrice(price, currency)}</span>
    </span>
  )

  const buttonProps: ClipboardBuyButtonType = {
    colorScheme: priceSource,
    justifyContent: 'left',
    maxwidth: true,
    size: 'sm',
  }

  return (
    <li>
      {isScg ? (
        <ClipboardBuyLinksButtonSCG buttonProps={buttonProps} decklist={decklist} onTrack={onTrack}>
          {content}
        </ClipboardBuyLinksButtonSCG>
      ) : isCardsphere ? (
        <ClipboardBuyLinksButtonCardsphere buttonProps={buttonProps} decklist={decklist} onTrack={onTrack}>
          {content}
        </ClipboardBuyLinksButtonCardsphere>
      ) : url && price && decklist.length > 0 ? (
        <ButtonDefault
          {...buttonProps}
          as='LinkOutbound'
          href={url}
          onClick={() => onTrack(url)}
          utm={{ medium: 'clipboard' }}
        >
          {content}
        </ButtonDefault>
      ) : (
        <ButtonDefault {...buttonProps} as='div' disabled>
          {content}
        </ButtonDefault>
      )}
    </li>
  )
}
